import React from 'react';
import { Body1, GetColor, InfoLink, TooltipPosition, getHelpCenterLinks } from 'venn-ui-kit';
import { useHasFF, withSuspense } from 'venn-utils';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { blockSettings, selectedBlocksState, studioLeftPanelOpen } from 'venn-state';
import BlockTabs from './tabs/BlockTabs';
import styled from 'styled-components';
import { ClosePanelButton } from 'venn-components';
import type { CustomBlockTypeEnum } from 'venn-utils';
import { cn } from '@/lib/utils';

// TODO(will): Change this back to constant map post macro release
const getBlockDefinitionLinks = (): Partial<Record<CustomBlockTypeEnum, string>> => {
  const links = getHelpCenterLinks();
  return {
    GROWTH_SIMULATION: links.GROWTH_SIMULATOR_FAQ_HREF,
    SCENARIO: links.SENSITIVITY_ANALYSIS_FAQ_HREF,
    NOTABLE_PERIODS: links.NOTABLE_PERIODS_FAQ_HREF,
    PORTFOLIO_COMPARISON: links.PORTFOLIO_COMPARISON_HELP_HREF,
    PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN: links.PUBLIC_PRIVATE_ASSET_GROWTH_HREF,
    PUBLIC_PRIVATE_ASSET_GROWTH_PERCENTILES: links.PUBLIC_PRIVATE_ASSET_GROWTH_HREF,
    CORRELATION: links.INVESTMENT_CORRELATION_TOOL_HREF,
    PEER_GROUPS: links.PEER_GROUP_ANALYSIS_HELP_HREF,
    ASSET_EXPOSURE: links.HOLDINGS_FAQ_HREF,
    GEOGRAPHY_EXPOSURE: links.HOLDINGS_FAQ_HREF,
    SECTOR_EXPOSURE: links.HOLDINGS_FAQ_HREF,
    PRIVATE_CASH_FLOW: links.PRIVATE_ASSET_CASH_FLOW_HELP_HREF,
    PRIVATE_CAPITAL_SUMMARY: links.PRIVATE_ASSET_CASH_FLOW_HELP_HREF,
    PRIVATE_PERFORMANCE_SUMMARY: links.PRIVATE_PERFORMANCE_HELP_HREF,
    PRIVATE_PERFORMANCE_TIME_SERIES: links.PRIVATE_PERFORMANCE_HELP_HREF,
  };
};

/** Content for the panel that allows users to adjust a selected block's configuration, settings, options, and other similar synonyms. */
export const StudioBlockConfigPanelContent = withSuspense(
  // TODO: SHIMMER: add a shimmer block during suspense
  null,
  React.memo(function StudioBlockConfigPanelContent({ selectedBlockId }: { selectedBlockId: string }) {
    const isMainLeftPanelExpanded = useRecoilValue(studioLeftPanelOpen);
    const setSelectedBlocks = useSetRecoilState(selectedBlocksState);

    return (
      <StudioBlockConfigWindow>
        <CloseButtonWrapper isMainLeftPanelExpanded={isMainLeftPanelExpanded}>
          <ClosePanelButton onClose={() => setSelectedBlocks([])} className="mr-[8px]" />
        </CloseButtonWrapper>
        <BlockConfigHeader selectedBlockId={selectedBlockId} />
        <BlockTabs selected={selectedBlockId} />
      </StudioBlockConfigWindow>
    );
  }),
);

const StudioBlockConfigWindow = styled.div`
  height: 100%;
  background-color: ${GetColor.White};
  border-left: 3px solid ${GetColor.GreyScale.Grey30};
  border-right: 1px solid ${GetColor.GreyScale.Grey30};
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

const CloseButtonWrapper = styled.div<{ isMainLeftPanelExpanded: boolean }>`
  position: absolute;
  top: ${({ isMainLeftPanelExpanded }) => (isMainLeftPanelExpanded ? 14 : 11)}px;
  right: 2px;
`;

const BlockConfigHeader = ({ selectedBlockId }: { selectedBlockId: string }) => {
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  const blockSetting = useRecoilValue(blockSettings(selectedBlockId));

  const blockDefinitionUrl = getBlockDefinitionLinks()[blockSetting.customBlockType];

  return (
    <div className="border-b border-x-0 border-t-0 border-solid border-venn-grey-30 h-[46px] pl-[10px] flex items-end">
      <Body1 className="inline-block">
        <div className="text-[13px] font-bold py-[4px] pl-[2px]">
          {`${blockSetting?.title} Block`}
          {blockDefinitionUrl && (
            <InfoLink
              portalTooltip
              to={blockDefinitionUrl}
              tooltip="Block Definition"
              tooltipPosition={TooltipPosition.Bottom}
            />
          )}
        </div>
        <div className={cn('h-[6px] w-full', hasBlockAssignment ? 'bg-venn-dark-blue' : 'bg-venn-primary-main')} />
      </Body1>
    </div>
  );
};
